import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Unified Tab Component with Theme Integration
 *
 * Usage:
 * <Tabs
 *   tabs={[
 *     { id: 'tab1', label: 'Tab 1', icon: Icon1 },
 *     { id: 'tab2', label: 'Tab 2', icon: Icon2 }
 *   ]}
 *   activeTab={activeTab}
 *   onTabChange={(tabId) => setActiveTab(tabId)}
 *   mobileVisibleTabs={['tab1', 'tab2']}  // Optional: tabs visible on mobile without menu
 * />
 *
 * ── GROUPS ──────────────────────────────────────────────────────────────────
 *
 * A tab may carry `children`, which collapses several related destinations into
 * one entry with a second row beneath it:
 *
 *   { id: 'letsplay', label: "Let's Play", icon: Ticket, children: [
 *     { id: 'programs', label: 'Programs', icon: Calendar },
 *     { id: 'play',     label: 'Open Play', icon: Ticket },
 *   ]}
 *
 * The CHILDREN keep their own ids and `activeTab` still names a child, never the
 * group. That is the whole point: an academy's tabs are addressable as
 * `?tab=programs`, and Stripe is handed that URL as the place to send a payer
 * back to. Introducing a group id as a real destination would have rewritten
 * every one of those links and broken the ones already out in the world, in
 * emails and in Stripe sessions mid-flight, to tidy up a menu.
 *
 * Groups are additive — a tab list without `children` renders exactly as before.
 */
const Tabs = ({
  tabs = [],
  activeTab,
  onTabChange,
  mobileVisibleTabs = [],  // Array of tab IDs to show on mobile (first 2-3 tabs)
  showMobileMenu = true,    // Whether to show mobile menu for overflow tabs
  className = ''
}) => {
  const { theme } = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);

  // Filter tabs for mobile quick access
  const visibleMobileTabs = tabs.filter(tab => mobileVisibleTabs.includes(tab.id));

  const childrenOf = (tab) => tab.children || [];
  const isGroup = (tab) => childrenOf(tab).length > 0;
  /** A group is lit when any of its destinations is the one on screen. */
  const holdsActive = (tab) => isGroup(tab)
    ? childrenOf(tab).some(c => c.id === activeTab)
    : activeTab === tab.id;
  /** Clicking a group goes to its first destination — a group is not a page. */
  const targetOf = (tab) => (isGroup(tab) ? childrenOf(tab)[0].id : tab.id);

  const openGroup = tabs.find(t => isGroup(t) && holdsActive(t)) || null;

  const handleTabClick = (tabId) => {
    onTabChange(tabId);
    setShowDropdown(false);
  };

  const renderTab = (tab, isMobile = false) => {
    const isActive = holdsActive(tab);
    const Icon = tab.icon;

    return (
      <button
        key={tab.id}
        onClick={() => handleTabClick(targetOf(tab))}
        aria-expanded={isGroup(tab) ? isActive : undefined}
        className={`
          ${theme.tabBase}
          ${isActive ? theme.tabActive : theme.tabInactive}
          ${tab.className || ''}
          flex items-center gap-2 whitespace-nowrap
        `}
        disabled={tab.disabled}
      >
        {Icon && <Icon className={`w-4 h-4 ${tab.isLive ? 'text-red-500 animate-pulse' : ''}`} />}
        {isMobile ? (tab.shortLabel || tab.label) : tab.label}
        {tab.badge && (
          <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-red-500 text-white">
            {tab.badge}
          </span>
        )}
        {tab.onClose && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              tab.onClose();
            }}
            className="ml-1 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded cursor-pointer inline-flex items-center"
            role="button"
            aria-label="Close tab"
          >
            <X className="w-3 h-3" />
          </span>
        )}
      </button>
    );
  };

  /**
   * The open group's destinations, as a second row.
   *
   * Rendered for both widths rather than desktop-only: on a phone the group is
   * reached through the menu, and arriving at Programs with no visible way
   * across to Open Play or Courts would make the grouping a cost with no
   * benefit. Scrolls rather than wraps, so the row cannot push the page wide.
   */
  const renderGroupRow = () => {
    if (!openGroup) return null;
    return (
      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-2 -mt-1" role="tablist" aria-label={openGroup.label}>
        {childrenOf(openGroup).map((child) => {
          const Icon = child.icon;
          const on = activeTab === child.id;
          return (
            <button
              key={child.id}
              role="tab"
              aria-selected={on}
              onClick={() => handleTabClick(child.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap flex items-center gap-1.5 transition border ${
                on
                  // ⚠️ THE CHILD ROW WAS MISSED. The strip above uses
                  // theme.tabActive (see the top-level branch), so a themed
                  // academy showed orange parent tabs and blue children —
                  // Programs / Open Play / Tournaments / Courts under Let's Play.
                  ? `${theme.tabActive} border-transparent`
                  : `${theme.border} ${theme.textSecondary} ${theme.cardHover}`
              }`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {child.label}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className={className}>
      {/* Desktop: Show all tabs */}
      <div className="hidden md:flex gap-2 overflow-x-auto scrollbar-hide pb-2">
        {tabs.map(tab => renderTab(tab))}
      </div>

      <div className="hidden md:block">{renderGroupRow()}</div>

      {/* Mobile: Show menu button + quick access tabs */}
      {showMobileMenu && (
        <div className="md:hidden relative">
          <div className="flex gap-2 pb-2">
            {/* Hamburger Menu Button */}
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className={`${theme.tabBase} ${theme.tabInactive} flex items-center gap-2`}
            >
              <Menu className="w-4 h-4" />
              Menu
            </button>

            {/* Quick access tabs on mobile */}
            <div className="flex overflow-x-auto gap-2 scrollbar-hide">
              {visibleMobileTabs.map(tab => renderTab(tab, true))}
            </div>
          </div>

          {/* Dropdown Menu */}
          {showDropdown && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black/50 z-40"
                onClick={() => setShowDropdown(false)}
              />

              {/* Dropdown Content */}
              <div className={`absolute top-full left-0 right-0 mt-2 ${theme.card} rounded-xl shadow-xl z-50 overflow-hidden border ${theme.border} max-h-[70vh] overflow-y-auto`}>
                <div className="py-2">
                  {tabs.flatMap((tab) => {
                    // A group contributes a heading and its destinations, not a
                    // row of its own: the group is not somewhere you can be.
                    if (isGroup(tab)) {
                      const GroupIcon = tab.icon;
                      return [
                        <div key={`${tab.id}-head`} className={`px-4 pt-3 pb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide ${theme.textMuted}`}>
                          {GroupIcon && <GroupIcon className="w-3.5 h-3.5" />}
                          {tab.label}
                        </div>,
                        ...childrenOf(tab).map((child) => {
                          const ChildIcon = child.icon;
                          return (
                            <button
                              key={child.id}
                              onClick={() => handleTabClick(child.id)}
                              className={`w-full pl-9 pr-4 py-3 text-left ${activeTab === child.id ? `${theme.link} font-semibold` : theme.text} ${theme.cardHover} transition-colors flex items-center gap-3`}
                            >
                              {ChildIcon && <ChildIcon className="w-4 h-4" />}
                              {child.label}
                            </button>
                          );
                        }),
                      ];
                    }
                    const Icon = tab.icon;
                    return [(
                      <button
                        key={tab.id}
                        onClick={() => handleTabClick(tab.id)}
                        className={`w-full px-4 py-3 text-left ${theme.text} ${theme.cardHover} transition-colors flex items-center gap-3`}
                        disabled={tab.disabled}
                      >
                        {Icon && <Icon className={`w-4 h-4 ${tab.isLive ? 'text-red-500 animate-pulse' : ''}`} />}
                        {tab.label}
                        {tab.badge && (
                          <span className="ml-auto px-1.5 py-0.5 text-xs rounded-full bg-red-500 text-white">
                            {tab.badge}
                          </span>
                        )}
                        {tab.onClose && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              tab.onClose();
                              setShowDropdown(false);
                            }}
                            className="ml-auto p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded cursor-pointer inline-flex items-center"
                            role="button"
                            aria-label="Close tab"
                          >
                            <X className="w-3 h-3" />
                          </span>
                        )}
                      </button>
                    )];
                  })}
                </div>
              </div>
            </>
          )}

          {renderGroupRow()}
        </div>
      )}

      {/* Simple mode: just show tabs horizontally (no mobile menu) */}
      {!showMobileMenu && (
        <div className="md:hidden flex gap-2 overflow-x-auto scrollbar-hide pb-2">
          {tabs.map(tab => renderTab(tab, true))}
        </div>
      )}
    </div>
  );
};

export default Tabs;
